"use client"

import { useState, useEffect, useContext, useRef } from "react"
import { ErpContext } from "../../../context/ErpContext.js"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  Card,
  CardContent,
  Button,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import PersonIcon from "@mui/icons-material/Person"
import BusinessIcon from "@mui/icons-material/Business"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import PaymentIcon from "@mui/icons-material/Payment"
import ReceiptIcon from "@mui/icons-material/Receipt"
import Chip from "@mui/material/Chip"
import Tooltip from "@mui/material/Tooltip"

const ModalDetalleCuentaCorriente = ({ open, handleClose, cuentaCorriente, tabActivo = "adelantos" }) => {
  const theme = useTheme()
  const { getPlanCuotaPasajeroByTransaccionIds } = useContext(ErpContext)

  const [transaccionesData, setTransaccionesData] = useState([])
  const [transaccionesStats, setTransaccionesStats] = useState({
    totalCompras: 0,
    totalPagos: 0,
    ultimaTransaccion: null,
  })
  const lastEnrichKeyRef = useRef("")

  const esProveedor =
    tabActivo === "deudas" ||
    (cuentaCorriente?.tipoEntidad || "").toLowerCase().includes("proveedor")
  const esTabDeuda = tabActivo === "deudas"
  const infoTitle = esProveedor ? "Información del Proveedor" : "Información del Cliente"
  const modalTitle = esTabDeuda ? "Detalle de Deuda" : "Detalle de Cuenta Corriente"

  // Resetear guard cuando se abre/cierra el modal
  useEffect(() => {
    if (!open) {
      lastEnrichKeyRef.current = ""
    }
  }, [open])

  // Procesar datos base de transacciones
  useEffect(() => {
    if (!cuentaCorriente) return
    const transacciones = Array.isArray(cuentaCorriente.transacciones) ? cuentaCorriente.transacciones : []

    const transaccionesQueGeneranDeuda = transacciones.filter(
      (t) => t.operacionParaCuentaCorriente !== "operacionCaja"
    )
    const transaccionesQueSaldanDeuda = transacciones.filter(
      (t) => t.operacionParaCuentaCorriente === "operacionCaja"
    )
    const totalCompras = transaccionesQueGeneranDeuda.reduce((sum, t) => sum + Math.abs(t.montoTotal || 0), 0)
    const totalPagos = transaccionesQueSaldanDeuda.reduce((sum, t) => sum + Math.abs(t.montoTotal || 0), 0)

    const sortedTransacciones = [...transacciones].sort((a, b) => {
      const db = parseDateSafe(b.fecha)?.getTime() || 0
      const da = parseDateSafe(a.fecha)?.getTime() || 0
      return db - da
    })

    setTransaccionesData(sortedTransacciones)
    setTransaccionesStats({
      totalCompras,
      totalPagos,
      ultimaTransaccion: sortedTransacciones[0] || null,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuentaCorriente])

  // Enriquecer transacciones de clientes con grupo/destino/cuota
  useEffect(() => {
    const enriquecer = async () => {
      if (!cuentaCorriente || esProveedor || esTabDeuda) return
      const transacciones = cuentaCorriente.transacciones || []
      if (transacciones.length === 0) return

      try {
        const transaccionIds = transacciones
          .map((tx) => Number(tx.id))
          .filter((id) => Number.isFinite(id))
        if (transaccionIds.length === 0) return

        const key = `${cuentaCorriente.idEntidad || ""}-${transaccionIds.join(",")}`
        if (lastEnrichKeyRef.current === key) return
        lastEnrichKeyRef.current = key

        console.log("🔍 Enriqueciendo transacciones. IDs:", transaccionIds)
        const cuotas = await getPlanCuotaPasajeroByTransaccionIds(transaccionIds)
        console.log("📦 Cuotas recibidas:", cuotas)

        const cuotaPorTransaccion = new Map()
        ;(cuotas || []).forEach((c) => {
          if (c.idTransaccion) {
            const idTx = Number(c.idTransaccion)
            if (Number.isFinite(idTx)) {
              cuotaPorTransaccion.set(idTx, c)
            }
          }
        })
        console.log("🗺️ Map de cuotas creado con", cuotaPorTransaccion.size, "entradas")

        setTransaccionesData((prev) => {
          const enriched = []
          prev.forEach((tx) => {
            const cuota = cuotaPorTransaccion.get(Number(tx.id))
            
            // IMPORTANTE: Siempre agregar la transacción, tenga o no cuota asociada
            const grupoDescripcion = cuota?.item?.descripcion || tx.grupoDescripcion || tx.grupo || "-"
            const destinoDescripcion =
              cuota?.ubicacion?.descripcion ||
              tx.destinoDescripcion ||
              tx.ubicacionDescripcion ||
              tx.ubicacion?.descripcion ||
              "-"

            enriched.push({
              ...tx,
              cuotaNro: cuota?.cuotaNro ?? tx.cuotaNro ?? "-",
              idItem: cuota?.idItem ?? tx.idItem,
              idUbicacion: cuota?.idUbicacion ?? tx.idUbicacion,
              grupoDescripcion,
              destinoDescripcion,
              ubicacionDescripcion: destinoDescripcion,
            })
          })
          return enriched
        })
      } catch (error) {
        console.error("❌ Error enriqueciendo transacciones de cliente:", error)
      }
    }

    enriquecer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuentaCorriente, esProveedor, esTabDeuda])

  // Utilidades
  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(value || 0)

  const parseDateSafe = (value) => {
    if (!value) return null
    const d = new Date(value)
    if (!isNaN(d.getTime())) return d
    if (typeof value === "string") {
      const parts = value.split("/")
      if (parts.length === 3) {
        const [dd, mm, yyyy] = parts
        const d2 = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
        if (!isNaN(d2.getTime())) return d2
      }
    }
    return null
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const dateObj = parseDateSafe(dateString)
    if (!dateObj) return String(dateString)
    return new Intl.DateTimeFormat("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(dateObj)
  }

  const obtenerGrupo = (tx) => tx?.grupoDescripcion || tx?.grupo || tx?.grupoNombre || "-"
  const obtenerDestino = (tx) =>
    tx?.destinoDescripcion ||
    tx?.destino ||
    tx?.ubicacionDescripcion ||
    tx?.ubicacion?.descripcion ||
    "-"
  const obtenerCuotaNro = (tx) => {
    const cuota = tx?.cuotaNro ?? tx?.cuotaNumero ?? tx?.numeroCuota
    return cuota !== undefined && cuota !== null ? cuota : "-"
  }

  // 🏷️ Función para clasificar transacciones
  const clasificarTransaccion = (tx) => {
    // Tab "Deudas" (Legacy)
    if (esTabDeuda) {
      const esOperacionCaja = tx.operacionParaCuentaCorriente === "operacionCaja"
      const esTipo8 = tx.idTipoTransaccion === 8
      
      if (esOperacionCaja) {
        return {
          tipo: "pago",
          label: "Pago",
          color: "success",
          icono: <PaymentIcon sx={{ fontSize: 16 }} />,
          signo: "-",
          colorMonto: theme.palette.success.main,
          bgMonto: alpha(theme.palette.success.main, 0.1),
          tooltip: "Pago realizado que reduce la deuda pendiente con el proveedor"
        }
      }
      
      if (esTipo8 || tx.operacionParaCuentaCorriente === "operacionCC") {
        return {
          tipo: "deuda",
          label: "Deuda",
          color: "error",
          icono: <TrendingUpIcon sx={{ fontSize: 16 }} />,
          signo: "+",
          colorMonto: theme.palette.error.main,
          bgMonto: alpha(theme.palette.error.main, 0.1),
          tooltip: "Gasto registrado que aumenta lo que le debes al proveedor"
        }
      }
      
      // Fallback
      return {
        tipo: "deuda",
        label: "Deuda",
        color: "error",
        icono: <TrendingUpIcon sx={{ fontSize: 16 }} />,
        signo: "+",
        colorMonto: theme.palette.error.main,
        bgMonto: alpha(theme.palette.error.main, 0.1),
        tooltip: "Gasto registrado que aumenta lo que le debes al proveedor"
      }
    }
    
    // Tab "Cuenta Corriente" - Proveedor
    if (esProveedor) {
      const esAdelanto = tx.operacionParaCuentaCorriente === "adelantoCC"
      
      if (esAdelanto) {
        return {
          tipo: "adelanto",
          label: "Adelanto",
          color: "info",
          icono: <TrendingUpIcon sx={{ fontSize: 16 }} />,
          signo: "+",
          colorMonto: theme.palette.info.main,
          bgMonto: alpha(theme.palette.info.main, 0.1),
          tooltip: "Dinero adelantado al proveedor que aumenta su crédito disponible"
        }
      }
      
      // Si tiene grupo/destino, es un servicio consumido
      const tieneGrupo = tx.grupoDescripcion || tx.grupo || tx.idItem
      if (tieneGrupo) {
        return {
          tipo: "consumido",
          label: "Consumido",
          color: "warning",
          icono: <TrendingDownIcon sx={{ fontSize: 16 }} />,
          signo: "-",
          colorMonto: theme.palette.warning.main,
          bgMonto: alpha(theme.palette.warning.main, 0.1),
          tooltip: "Servicio contratado que consume del crédito adelantado al proveedor"
        }
      }
      
      // Fallback: asumir adelanto
      return {
        tipo: "adelanto",
        label: "Adelanto",
        color: "info",
        icono: <TrendingUpIcon sx={{ fontSize: 16 }} />,
        signo: "+",
        colorMonto: theme.palette.info.main,
        bgMonto: alpha(theme.palette.info.main, 0.1),
        tooltip: "Dinero adelantado al proveedor que aumenta su crédito disponible"
      }
    }
    
    // Tab "Cuenta Corriente" - Cliente
    const tieneCuotaPagada = tx.idTransaccion != null && tx.cuotaNro !== "-"
    
    if (tieneCuotaPagada) {
      return {
        tipo: "cuotaPagada",
        label: "Cuota Pagada",
        color: "success",
        icono: <PaymentIcon sx={{ fontSize: 16 }} />,
        signo: "-",
        colorMonto: theme.palette.success.main,
        bgMonto: alpha(theme.palette.success.main, 0.1),
        tooltip: "Cuota pagada por el cliente que reduce su deuda pendiente"
      }
    }
    
    // Cuota pendiente
    return {
      tipo: "cuotaCreada",
      label: "Cuota Creada",
      color: "warning",
      icono: <ReceiptIcon sx={{ fontSize: 16 }} />,
      signo: "+",
      colorMonto: theme.palette.warning.main,
      bgMonto: alpha(theme.palette.warning.main, 0.1),
      tooltip: "Cuota generada que representa la deuda pendiente del cliente"
    }
  }

  const columnasTransacciones = esTabDeuda
    ? ["ID", "Fecha", "Tipo", "Descripción", "Monto", "Medio de Pago", "Acciones"]
    : esProveedor
    ? ["ID", "Fecha", "Tipo", "Grupo", "Destino", "Monto", "Acciones"]
    : ["ID", "Fecha", "Tipo", "Grupo", "Destino", "Cuota Nro", "Monto", "Acciones"]

  if (!cuentaCorriente) return null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: theme.palette.primary.main, color: "white" }}>
        {modalTitle}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    {esProveedor ? <BusinessIcon sx={{ mr: 1 }} /> : <PersonIcon sx={{ mr: 1 }} />} {infoTitle}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Nombre:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {(cuentaCorriente.entidadCompleta?.descripcion || "N/A") +
                          " " +
                          (cuentaCorriente.entidadCompleta?.apellido || "")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        DNI/CUIT/CUIL:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {cuentaCorriente.entidadCompleta?.dniCuitCuil || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Teléfono:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {cuentaCorriente.entidadCompleta?.telefono || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    Resumen de Cuenta
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    {esTabDeuda ? (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Saldo (legacy):
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color={theme.palette.primary.main}>
                            {formatCurrency(cuentaCorriente.saldo || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Positivo: negocio debe al proveedor. Negativo: proveedor debe al negocio.
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Transacciones:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {transaccionesData.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Última transacción:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {transaccionesStats.ultimaTransaccion
                              ? formatDate(transaccionesStats.ultimaTransaccion.fecha)
                              : "N/A"}
                          </Typography>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Monto acumulado:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color={theme.palette.warning.main}>
                            {formatCurrency(cuentaCorriente.monto || cuentaCorriente.montoAcumulado || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Adelantos a proveedores o precio total del paquete.
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Cobrado/Consumido:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color={theme.palette.success.main}>
                            {formatCurrency(cuentaCorriente.cobrado || cuentaCorriente.cobradoAcumulado || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Servicios prestados (proveedores) o cuotas pagadas (clientes).
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Saldo dinámico:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color={theme.palette.primary.main}>
                            {formatCurrency(
                              cuentaCorriente.saldoCalculado ||
                                cuentaCorriente.saldoDinamico ||
                                (cuentaCorriente.monto || 0) - (cuentaCorriente.cobrado || 0)
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Positivo: crédito disponible (proveedor) o deuda pendiente (cliente).
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 460 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columnasTransacciones.map((col) => (
                  <TableCell key={col}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {transaccionesData.map((tx) => {
                const clasificacion = clasificarTransaccion(tx)
                
                return (
                  <TableRow
                    key={tx.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <TableCell>{tx.id}</TableCell>
                    <TableCell>{formatDate(tx.fecha)}</TableCell>
                    
                    {/* ✨ Nueva columna "Tipo" con chip y tooltip */}
                    <TableCell>
                      <Tooltip title={clasificacion.tooltip} arrow placement="top">
                        <Chip
                          icon={clasificacion.icono}
                          label={clasificacion.label}
                          color={clasificacion.color}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                        />
                      </Tooltip>
                    </TableCell>

                    {esTabDeuda ? (
                      <>
                        <TableCell>{tx.descripcion || "Sin descripción"}</TableCell>
                        {/* ✨ Monto con signo y color */}
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: clasificacion.bgMonto,
                              color: clasificacion.colorMonto,
                              fontWeight: 700,
                            }}
                          >
                            {clasificacion.signo}{formatCurrency(Math.abs(tx.montoTotal || 0))}
                          </Box>
                        </TableCell>
                        <TableCell>{tx.medioPago || tx.medioDePago || "-"}</TableCell>
                      </>
                    ) : esProveedor ? (
                      <>
                        <TableCell>{obtenerGrupo(tx)}</TableCell>
                        <TableCell>{obtenerDestino(tx)}</TableCell>
                        {/* ✨ Monto con signo y color */}
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: clasificacion.bgMonto,
                              color: clasificacion.colorMonto,
                              fontWeight: 700,
                            }}
                          >
                            {clasificacion.signo}{formatCurrency(Math.abs(tx.montoTotal || 0))}
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{obtenerGrupo(tx)}</TableCell>
                        <TableCell>{obtenerDestino(tx)}</TableCell>
                        <TableCell>{obtenerCuotaNro(tx)}</TableCell>
                        {/* ✨ Monto con signo y color */}
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: clasificacion.bgMonto,
                              color: clasificacion.colorMonto,
                              fontWeight: 700,
                            }}
                          >
                            {clasificacion.signo}{formatCurrency(Math.abs(tx.montoTotal || 0))}
                          </Box>
                        </TableCell>
                      </>
                    )}

                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PictureAsPdfIcon />}
                        disabled
                        sx={{
                          minWidth: "auto",
                          px: 1,
                          py: 0.5,
                          fontSize: "0.75rem",
                          color: theme.palette.text.secondary,
                          borderColor: theme.palette.text.secondary,
                        }}
                      >
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
              {transaccionesData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnasTransacciones.length} align="center">
                    No hay transacciones disponibles
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  )
}

export default ModalDetalleCuentaCorriente
